import { useState } from "react";
import { useListEconomatPaiements, useListEconomatEcoles } from "@/lib/backoffice-hooks";
import { usePaginationState } from "@/lib/use-paginated-query";
import { PaginationControls } from "@/components/PaginationControls";
import { formatDateShort } from "@/lib/format";
import EconomatShell from "@/components/layout/EconomatShell";
import { Card } from "@/components/ui/card";
import { Loader2, Download, FileSpreadsheet, Eye } from "lucide-react";
import { downloadRecuPdf, downloadCsv } from "@/lib/downloadRecu";
import RecuPreviewDialog from "@/components/RecuPreviewDialog";
import ReconciliationUnifiee from "@/components/ReconciliationUnifiee";

function fcfa(n: number) { return new Intl.NumberFormat("fr-FR").format(n) + " FCFA"; }
const STATUS_LABEL: Record<string, string> = { SUCCESS: "Encaissé", PROCESSING: "En cours", PENDING: "En attente", FAILED: "Échoué", CANCELLED: "Annulé", REFUNDED: "Remboursé" };
const STATUSES = ["", "SUCCESS", "PENDING", "PROCESSING", "FAILED", "CANCELLED"] as const;

export default function EconomatPaiements() {
  const { page, size, setPage, setSize, resetToFirstPage } = usePaginationState(20);
  const [status, setStatus] = useState<string>("");
  const [apercu, setApercu] = useState<{ id: string; reference: string } | null>(null);
  // Réconciliation unifiée : l'économat supervise plusieurs écoles → on réutilise
  // la liste des écoles du diocèse (même source que la page /economat/ecoles) pour
  // choisir l'établissement à inspecter.
  const [ecoleUnifie, setEcoleUnifie] = useState<string>("");
  const { data: ecolesData } = useListEconomatEcoles({ page: 0, size: 100 });
  const ecoleOptions = ecolesData?.items ?? [];

  const onStatusChange = (s: string) => { setStatus(s); resetToFirstPage(); };

  const { data, isLoading } = useListEconomatPaiements({ status: status || undefined, page, size });
  const paiements = data?.items ?? [];
  const total = data?.total ?? 0;

  return (
    <EconomatShell>
      <div className="space-y-5">
        <header className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500">Paiements</div>
            <h1 className="text-2xl font-bold text-navy">{total} paiements</h1>
          </div>
          <div className="flex flex-wrap gap-1 items-center">
            {STATUSES.map((s) => (
              <button key={s || "all"} onClick={() => onStatusChange(s)} className={`px-3 py-1.5 text-xs rounded border ${status === s ? "bg-navy text-white border-navy" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`} data-testid={`filter-${s || "all"}`}>
                {/* v8 ignore start -- STATUSES est un const figé dont chaque entrée non vide existe dans STATUS_LABEL : le fallback `?? s` est défensif et inatteignable via le map */}
                {s ? (STATUS_LABEL[s] ?? s) : "Tous"}
                {/* v8 ignore stop */}
              </button>
            ))}
            <button
              onClick={() => {
                // Note : l'export CSV ne couvre que la page actuellement affichée (pagination serveur) — acceptable.
                const rows = paiements.map((p) => ({
                  Reference: p.reference,
                  Ecole: p.ecoleDenomination,
                  Eleve: p.eleveNom,
                  Matricule: p.eleveMatricule,
                  Motif: p.motifLibelle,
                  Montant_FCFA: p.montantXof,
                  Mode: p.methode,
                  Driver: p.driver,
                  Statut: STATUS_LABEL[p.status] ?? p.status,
                  Date_creation: p.createdAt ? new Date(p.createdAt).toLocaleString("fr-FR") : "",
                  Date_paiement: p.paidAt ? new Date(p.paidAt).toLocaleString("fr-FR") : "",
                }));
                downloadCsv(`paiements-diocese-${new Date().toISOString().slice(0, 10)}.csv`, rows);
              }}
              className="text-xs px-3 py-1.5 rounded border border-slate-300 bg-white hover:bg-slate-50 inline-flex items-center gap-1.5 ml-2"
              data-testid="button-export-csv"
            >
              <FileSpreadsheet className="w-3.5 h-3.5" /> Export CSV
            </button>
          </div>
        </header>

        <Card className="p-0 border border-slate-200 shadow-none overflow-hidden">
          {isLoading && <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-navy" /></div>}
          {!isLoading && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200 bg-slate-50">
                  <tr>
                    <th className="text-left px-4 py-2.5">Référence</th>
                    <th className="text-left">École</th>
                    <th className="text-left">Élève</th>
                    <th className="text-left">Motif</th>
                    <th className="text-right">Montant</th>
                    <th className="text-left pl-3">Mode</th>
                    <th className="text-left">Statut</th>
                    <th className="text-left">Date</th>
                    <th className="text-center pr-4">Reçu</th>
                  </tr>
                </thead>
                <tbody>
                  {paiements.length === 0 && <tr><td colSpan={9} className="py-8 text-center text-slate-500">Aucun paiement.</td></tr>}
                  {paiements.map((p) => (
                    <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`row-paiement-${p.reference}`}>
                      <td className="px-4 py-2 font-mono text-xs text-navy" style={{ fontFamily: "JetBrains Mono, monospace" }}>{p.reference || "·"}</td>
                      <td className="text-slate-700 truncate max-w-[200px]">{p.ecoleDenomination}</td>
                      <td>{p.eleveNom}</td>
                      <td className="text-slate-600">{p.motifLibelle}</td>
                      <td className="text-right font-mono" style={{ fontFamily: "JetBrains Mono, monospace" }}>{fcfa(p.montantXof)}</td>
                      <td className="pl-3 text-xs text-slate-600">{String(p.methode).replace(/_/g, " ")}</td>
                      <td>
                        <span className={`text-xs px-2 py-0.5 rounded ${p.status === "SUCCESS" ? "bg-emerald-100 text-emerald-700" : p.status === "FAILED" || p.status === "CANCELLED" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>
                          {STATUS_LABEL[p.status] ?? p.status}
                        </span>
                      </td>
                      <td className="text-xs text-slate-500">{formatDateShort(p.paidAt ?? p.createdAt)}</td>
                      <td className="text-center pr-4">
                        {p.status === "SUCCESS" && (
                          <div className="inline-flex items-center gap-0.5">
                            <button onClick={() => setApercu({ id: p.id, reference: p.reference })} title="Aperçu du reçu" className="text-slate-500 hover:text-navy p-1" data-testid={`button-apercu-${p.reference}`}>
                              <Eye className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={() => downloadRecuPdf(p.id, p.reference)} title="Télécharger le reçu PDF" className="text-slate-500 hover:text-navy p-1" data-testid={`button-pdf-${p.reference}`}>
                              <Download className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <PaginationControls page={page} size={size} total={total} onPageChange={setPage} onSizeChange={setSize} />
            </div>
          )}
        </Card>

        <div className="flex flex-wrap items-center gap-2">
          <label htmlFor="unifie-ecole" className="text-sm text-slate-600">Établissement :</label>
          <select
            id="unifie-ecole"
            value={ecoleUnifie}
            onChange={(e) => setEcoleUnifie(e.target.value)}
            className="text-sm border border-slate-300 rounded px-2 py-1.5 bg-white min-w-[260px]"
            data-testid="unifie-select-ecole"
          >
            <option value="">Choisir un établissement</option>
            {ecoleOptions.map((e) => (
              <option key={e.id} value={e.id}>{e.denomination}</option>
            ))}
          </select>
        </div>

        <ReconciliationUnifiee etablissementId={ecoleUnifie || undefined} />
      </div>

      <RecuPreviewDialog
        open={apercu !== null}
        onOpenChange={(o) => !o && setApercu(null)}
        source={{ kind: "auth", paiementId: apercu?.id ?? "" }}
        reference={apercu?.reference}
      />
    </EconomatShell>
  );
}
